import {
    AnimalSourceOrigin,
    BaselineFeed,
    BaselineFeedWithOrigin,
} from "../Baseline";

import {
    FreshGrass as BaselineFreshGrass,
    CattleBeddingSystem as BaselineCattleBeddingSystem,
    CattleManureManagementSystem as BaselineCattleManureManagementSystem,
} from "../Baseline/CattleBaseline";

import {
    InterventionFeed,
    InterventionPropertyChangeMetric,
    InterventionPropertyValue
} from "./Intervention";

export enum TargetAnimal {
    COWS = "cows",
    CALVES = "calves",
}

export enum CattleBeddingType {
    Sand = "SAND",
    SawDust = "SAW_DUST",
    WheatStraw = "WHEAT_STRAW",
    WoodShavings = "WOOD_SHAVINGS",
}

export interface CattleBeddingSystem {
    id: string;

    beddingType?: CattleBeddingType;
    beddingType_newValue?: InterventionPropertyValue;
    beddingType_changeMetric?: InterventionPropertyChangeMetric;

    beddingAmount: InterventionPropertyValue;
    beddingAmount_newValue?: InterventionPropertyValue;
    beddingAmount_changeMetric?: InterventionPropertyChangeMetric;
}

export interface MothersMilk {
    isMothersMilk?: boolean;
    isMothersMilk_newValue?: InterventionPropertyChangeMetric;
    isMothersMilk_changeMetric?: InterventionPropertyChangeMetric;

    amount?: InterventionPropertyValue;
    amount_newValue?: InterventionPropertyValue;
    amount_changeMetric?: InterventionPropertyChangeMetric;

    crudeProteinContent?: InterventionPropertyValue;
    crudeProteinContent_newValue?: InterventionPropertyValue;
    crudeProteinContent_changeMetric?: InterventionPropertyChangeMetric;

    digestibility?: InterventionPropertyValue;
    digestibility_newValue?: InterventionPropertyValue;
    digestibility_changeMetric?: InterventionPropertyChangeMetric;

    dryMatterContent?: InterventionPropertyValue;
    dryMatterContent_newValue?: InterventionPropertyValue;
    dryMatterContent_changeMetric?: InterventionPropertyChangeMetric;
}

export interface FreshGrass extends MothersMilk {
    id?: string;

    type?: string;
    type_newValue?: InterventionPropertyValue;
    type_changeMetric?: InterventionPropertyChangeMetric;

    grossEnergyContent?: InterventionPropertyValue;
    grossEnergyContent_newValue?: InterventionPropertyValue;
    grossEnergyContent_changeMetric?: InterventionPropertyChangeMetric;

    urinaryEnergy?: InterventionPropertyValue;
    urinaryEnergy_newValue?: InterventionPropertyValue;
    urinaryEnergy_changeMetric?: InterventionPropertyChangeMetric;

    ashContent?: InterventionPropertyValue;
    ashContent_newValue?: InterventionPropertyValue;
    ashContent_changeMetric?: InterventionPropertyChangeMetric;

    neutralDetergentFiber?: InterventionPropertyValue;
    neutralDetergentFiber_newValue?: InterventionPropertyValue;
    neutralDetergentFiber_changeMetric?: InterventionPropertyChangeMetric;
}

export interface CattleFeed {
    databaseFoundation: string;

    dietCharacterisation?:
    | "FEEDLOT_CORN"
    | "FEEDLOT_OTHER_GRAIN"
    | "HIGH_FORAGE_HIGH_QUALITY"
    | "HIGH_FORAGE_LOW_QUALITY"
    | "MIXED_RATIONS";
    dietCharacterisation_newValue?: InterventionPropertyValue;
    dietCharacterisation_changeMetric?: InterventionPropertyChangeMetric;

    averageMilkFat?: InterventionPropertyValue;
    averageMilkFat_newValue?: InterventionPropertyValue;
    averageMilkFat_changeMetric?: InterventionPropertyChangeMetric;

    mothersMilk?: MothersMilk;

    compoundFeeds?: InterventionFeed[] | null;
    singleIngredients?: InterventionFeed[] | null;
    freshGrass?: FreshGrass[] | null;

    compoundFeedsAdditions?: BaselineFeed[] | null;
    singleIngredientsAdditions?: BaselineFeedWithOrigin[] | null;
    freshGrassAdditions?: BaselineFreshGrass[] | null;
}

export enum CattleManureManagementSystemType {
    // NO STORAGE
    AerobicForced = "AEROBIC_FORCED",
    AerobicNatural = "AEROBIC_NATURAL",
    AnaerobicHighLeakHighGastight = "ANAEROBIC_HIGH_LEAK_HIGH_GASTIGHT",
    AnaerobicHighLeakLowGastight = "ANAEROBIC_HIGH_LEAK_LOW_GASTIGHT",
    AnaerobicHighLeakOpen = "ANAEROBIC_HIGH_LEAK_OPEN",
    AnaerobicLowLeakHighGastight = "ANAEROBIC_LOW_LEAK_HIGH_GASTIGHT",
    AnaerobicLowLeakLowGastight = "ANAEROBIC_LOW_LEAK_LOW_GASTIGHT",
    AnaerobicLowLeakOpen = "ANAEROBIC_LOW_LEAK_OPEN",
    CompostingIntensiveWindrow = "COMPOSTING_INTENSIVE_WINDROW",
    CompostingInVessel = "COMPOSTING_IN_VESSEL",
    CompostingPassiveWindrow = "COMPOSTING_PASSIVE_WINDROW",
    CompostingStaticPile = "COMPOSTING_STATIC_PILE",
    DailySpread = "DAILY_SPREAD",

    // OVER / UNDER 1 MONTH
    DeepBeddingActiveMixing = "DEEP_BEDDING_ACTIVE_MIXING",
    DeepBeddingNoMixing = "DEEP_BEDDING_NO_MIXING",

    // 1 to 12
    LiquidCover = "LIQUID_COVER",
    LiquidCrust = "LIQUID_CRUST",
    LiquidNoCrust = "LIQUID_NO_CRUST",
    Pit = "PIT",

    // NO STORAGE
    SolidAdditives = "SOLID_ADDITIVES",
    SolidBulking = "SOLID_BULKING",
    SolidCovered = "SOLID_COVERED",
    SolidStorage = "SOLID_STORAGE",
    UncoveredAnaerobicLagoon = "UNCOVERED_ANAEROBIC_LAGOON",
}

export enum StoragePeriod {
    ONE_MONTH = "1 month",
    THREE_MONTHS = "3 month",
    FOUR_MONTHS = "4 month",
    SIX_MONTHS = "6 month",
    TWELVE_MONTHS = "12 month",
    OVER_ONE_MONTH = "over 1 month",
    UNDER_ONE_MONTH = "under 1 month",
}

export enum ManurePhase {
    LiquidSlurry = "LIQUID_SLURRY",
    Solid = "SOLID",
}

export interface CattleManureManagementSystem {
    id: string;

    mmsType?: CattleManureManagementSystemType;
    mmsType_newValue?: InterventionPropertyValue;
    mmsType_changeMetric?: InterventionPropertyChangeMetric;

    storagePeriod?: StoragePeriod;
    storagePeriod_newValue?: InterventionPropertyValue;
    storagePeriod_changeMetric?: InterventionPropertyChangeMetric;

    share: InterventionPropertyValue;
    share_newValue?: InterventionPropertyValue;
    share_changeMetric?: InterventionPropertyChangeMetric;

    manurePhase?: ManurePhase;
    manurePhase_newValue?: InterventionPropertyValue;
    manurePhase_changeMetric?: InterventionPropertyChangeMetric;
}

export interface CattleHousingAndManure {
    timeInFeedlot: InterventionPropertyValue;
    timeInFeedlot_newValue?: InterventionPropertyValue;
    timeInFeedlot_changeMetric?: InterventionPropertyChangeMetric;

    timeInHousing: InterventionPropertyValue;
    timeInHousing_newValue?: InterventionPropertyValue;
    timeInHousing_changeMetric?: InterventionPropertyChangeMetric;

    timeGrazingLargeAreas: InterventionPropertyValue;
    timeGrazingLargeAreas_newValue?: InterventionPropertyValue;
    timeGrazingLargeAreas_changeMetric?: InterventionPropertyChangeMetric;

    timeGrazingPastures: InterventionPropertyValue;
    timeGrazingPastures_newValue?: InterventionPropertyValue;
    timeGrazingPastures_changeMetric?: InterventionPropertyChangeMetric;

    timeInHousingTied: InterventionPropertyValue;
    timeInHousingTied_newValue?: InterventionPropertyValue;
    timeInHousingTied_changeMetric?: InterventionPropertyChangeMetric;

    manureSystems: CattleManureManagementSystem[];
    beddingSystems?: CattleBeddingSystem[];

    manureSystemsAdditions: BaselineCattleManureManagementSystem[];
    beddingSystemsAdditions?: BaselineCattleBeddingSystem[];
}

export interface CattleHousing {
    beddingSystems?: CattleBeddingSystem[] | null;
    beddingSystemsAdditions?: BaselineCattleBeddingSystem[] | null;

    calves: Omit<CattleHousingAndManure, "beddingSystems">;
    cows: Omit<CattleHousingAndManure, "beddingSystems">;
}

export interface CattleEmission {
    methaneEntericFermentation?: InterventionPropertyValue;
    methaneEntericFermentation_newValue?: InterventionPropertyValue;
    methaneEntericFermentation_changeMetric?: InterventionPropertyChangeMetric;

    nmvoc?: InterventionPropertyValue;
    nmvoc_newValue?: InterventionPropertyValue;
    nmvoc_changeMetric?: InterventionPropertyChangeMetric;

    methaneMms?: InterventionPropertyValue;
    methaneMms_newValue?: InterventionPropertyValue;
    methaneMms_changeMetric?: InterventionPropertyChangeMetric;

    nitrousOxideLeaching?: InterventionPropertyValue;
    nitrousOxideLeaching_newValue?: InterventionPropertyValue;
    nitrousOxideLeaching_changeMetric?: InterventionPropertyChangeMetric;

    nitrousOxideDirect?: InterventionPropertyValue;
    nitrousOxideDirect_newValue?: InterventionPropertyValue;
    nitrousOxideDirect_changeMetric?: InterventionPropertyChangeMetric;

    nitrousOxideVolatilization?: InterventionPropertyValue;
    nitrousOxideVolatilization_newValue?: InterventionPropertyValue;
    nitrousOxideVolatilization_changeMetric?: InterventionPropertyChangeMetric;

    nitrogenOxidesStorage?: InterventionPropertyValue;
    nitrogenOxidesStorage_newValue?: InterventionPropertyValue;
    nitrogenOxidesStorage_changeMetric?: InterventionPropertyChangeMetric;

    ammoniaStorage?: InterventionPropertyValue;
    ammoniaStorage_newValue?: InterventionPropertyValue;
    ammoniaStorage_changeMetric?: InterventionPropertyChangeMetric;

    ammoniaHousing?: InterventionPropertyValue;
    ammoniaHousing_newValue?: InterventionPropertyValue;
    ammoniaHousing_changeMetric?: InterventionPropertyChangeMetric;

    ammoniaYard?: InterventionPropertyValue;
    ammoniaYard_newValue?: InterventionPropertyValue;
    ammoniaYard_changeMetric?: InterventionPropertyChangeMetric;

    pm2_5?: InterventionPropertyValue;
    pm2_5_newValue?: InterventionPropertyValue;
    pm2_5_changeMetric?: InterventionPropertyChangeMetric;

    pm10?: InterventionPropertyValue;
    pm10_newValue?: InterventionPropertyValue;
    pm10_changeMetric?: InterventionPropertyChangeMetric;

    tsp?: InterventionPropertyValue;
    tsp_newValue?: InterventionPropertyValue;
    tsp_changeMetric?: InterventionPropertyChangeMetric;
}

export interface ExternalSource {
    id: string;
    distanceTruck?: InterventionPropertyValue;
    distanceTruck_newValue?: InterventionPropertyValue;
    distanceTruck_changeMetric?: InterventionPropertyChangeMetric;

    numberAnimals: InterventionPropertyValue;
    numberAnimals_newValue: InterventionPropertyValue;
    numberAnimals_changeMetric: InterventionPropertyChangeMetric;

    origin: AnimalSourceOrigin;

    shrinkingRate?: InterventionPropertyValue;
    shrinkingRate_newValue?: InterventionPropertyValue;
    shrinkingRate_changeMetric?: InterventionPropertyChangeMetric;
}

export interface InternalSource extends ExternalSource {
    farmId: string;
    farmName: string;
    originStageId: string;
    stageName: string;
    baselineRef?: string;
    animalType?: string;
    animalSystemType?: string;
    stageType: string;
}